import { LocaleEnum, localeNames } from '@systemeio/ui-frontend'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useLanguageSwitcher } from 'shared/hooks/use-language-switcher'
import { useToggle } from 'shared/hooks/use-toggle'
import useUser from 'shared/hooks/use-user'
import ChevronIcon from 'shared/icons/chevron-icon'
import { classWithModifiers } from 'shared/utils/className-with-modifiers'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'

interface LanguageSwitcherProps {
  className?: string
  listClassName?: string
  listItemClassName?: string
  buttonClassName?: string
}

export function AuthenticationLanguageSwitcher({
  className,
  listClassName,
  listItemClassName,
  buttonClassName,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const { user, loading } = useUser()
  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()
  const [isOpen, toggle, setIsOpen] = useToggle()
  const dropdownRef = useClickOutside(() => setIsOpen(false))

  const changeLanguage = async (lang: LocaleEnum) => {
    await handleChangeLanguage(lang, { forceRouterUpdate: true })
  }

  return (
    <div
      className={twMerge(
        'group relative h-full text-background-secondary/90',
        className,
        classWithModifiers(addPublicCustomClassNamePrefix('language-switcher'), {
          ['loading']: loading,
        }),
      )}
    >
      {loading ? (
        <div className="mt-5 size-6 animate-pulse rounded-sm bg-skeleton-primary" />
      ) : (
        user && (
          <button
            className={twMerge(
              'flex h-full items-center justify-around gap-2.5 border-b-4 border-transparent text-sm font-bold focus:outline-none focus-visible:border-brand-primary',
              buttonClassName,
            )}
            onClick={toggle}
            ref={dropdownRef}
          >
            <span className={twJoin(isChangingLanguage && 'shimmer-loading')}>
              {i18n?.language ? getCalculatedLanguage(i18n.language) : ''}
            </span>
            <ChevronIcon className="size-3" />
          </button>
        )
      )}
      <div
        className={twJoin(
          'customScroll absolute -left-24 top-6 z-50 max-h-[400px] overflow-hidden overflow-y-auto opacity-0 transition-all',
          isOpen ? 'visible opacity-100' : 'invisible opacity-0',
        )}
      >
        <div className="absolute right-[35px] top-[-12px] border-[6px] border-x-transparent border-b-background-secondary border-t-transparent" />
        <div
          className={twMerge(
            'flex flex-col rounded-lg bg-background-secondary py-4 text-sm shadow-lg',
            listClassName,
          )}
        >
          <ul>
            {Object.keys(localeNames)
              .filter(dashboardLocale => dashboardLocale !== i18n.language)
              .map(dashboardLocale => (
                <li key={dashboardLocale}>
                  <button
                    onClick={() => changeLanguage(dashboardLocale as LocaleEnum)}
                    className={twMerge(
                      'w-full cursor-pointer px-7 py-1 text-start text-text-tertiary hover:bg-background-primary/20 hover:text-brand-primary focus:outline-none focus-visible:text-brand-primary',
                      listItemClassName,
                    )}
                  >
                    {localeNames[dashboardLocale as LocaleEnum]}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
